/**
 * Period picker — preset chips + custom range + frequency.
 *
 * Replaces the per-page ToggleButtonGroup for selecting MTD/7D/30D/90D/YTD with
 * a richer control: in addition to those presets, users can pick a custom date
 * range and (optionally) the bucket frequency the data should be aggregated at.
 *
 * Shape of the value object the picker emits via `onChange`:
 *
 *   {
 *     code:      'mtd' | '7d' | '30d' | '90d' | 'ytd' | 'custom',
 *     start:     ISO 8601 string,                  // start of the window
 *     end:       ISO 8601 string,                  // exclusive end of the window
 *     frequency: '5min' | '30min' | 'hourly' | 'daily' | 'weekly' | 'monthly'
 *                | 'quarterly' | 'yearly' | null,  // bucket the data is aggregated at
 *     label:     'Last 30 days' | 'Custom · 01 Apr → 28 Apr' | ...
 *   }
 *
 * Screens whose data has a coarse native resolution pass `granularity` (the
 * finest meaningful bucket, e.g. 'monthly'); the picker then disables every
 * finer frequency and snaps auto-suggested/persisted values up to a supported
 * tier — so a quarterly curve view can only ever be bucketed monthly/quarterly/
 * yearly, never daily.
 *
 * Backends accept the same period codes today; the new fields (`start`, `end`,
 * `frequency`) will be wired into the backend in the next pass — for now the
 * frontend can decode `code` for preset values and pass `start`/`end` directly
 * for `custom`.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, ToggleButton, ToggleButtonGroup, Popover, Button, IconButton,
  TextField, Tooltip, Typography, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useThemeColors } from '../theme/ThemeModeContext';

// ── Preset definitions ─────────────────────────────────────────────────────

// Each preset carries its own [start, end) range function so screens can
// supply domain-appropriate quick-picks (a Fair Value screen wants multi-year
// horizons, an operational tab wants MTD/7D). `now` is a dayjs instance.
const DEFAULT_PRESETS = [
  { value: 'mtd', label: 'MTD', range: (now) => ({ start: now.startOf('month'),      end: now.endOf('day') }) },
  { value: '7d',  label: '7D',  range: (now) => ({ start: now.endOf('day').subtract(7,  'day'), end: now.endOf('day') }) },
  { value: '30d', label: '30D', range: (now) => ({ start: now.endOf('day').subtract(30, 'day'), end: now.endOf('day') }) },
  { value: '90d', label: '90D', range: (now) => ({ start: now.endOf('day').subtract(90, 'day'), end: now.endOf('day') }) },
  { value: 'ytd', label: 'YTD', range: (now) => ({ start: now.startOf('year'),       end: now.endOf('day') }) },
];

// Full frequency set. AEMO's canonical settlement interval is 30-min (the
// MMS feed roll-up); 5-min is the dispatch interval. Both are surfaced so the
// user can drill from settlement-grade down to dispatch-grade analysis. The
// coarser tiers (monthly → yearly) drive the bucketing of longer-horizon
// series — e.g. a quarterly PPA/curve view renders one bar per quarter.
const ALL_FREQUENCIES = [
  { value: '5min',      label: '5-minutely'  },
  { value: '30min',     label: '30-minutely' },
  { value: 'hourly',    label: 'Hourly'      },
  { value: 'daily',     label: 'Daily'       },
  { value: 'weekly',    label: 'Weekly'      },
  { value: 'monthly',   label: 'Monthly'     },
  { value: 'quarterly', label: 'Quarterly'   },
  { value: 'yearly',    label: 'Yearly'      },
];

// Canonical fine→coarse ordering used to reason about a screen's "granularity
// floor": a screen whose underlying data is monthly can be bucketed monthly,
// quarterly or yearly, but never daily. Index in this array == coarseness.
const FREQ_ORDER = ALL_FREQUENCIES.map((f) => f.value);
const freqIndex = (f) => FREQ_ORDER.indexOf(f);

/**
 * Snap a desired frequency to the set the screen actually supports. If the
 * desired tier is unavailable (e.g. auto-suggested 'daily' on a monthly-only
 * screen), fall to the nearest coarser enabled tier, else the finest enabled.
 */
function clampFrequency(freq, enabled) {
  if (!enabled.length) return freq;
  if (enabled.includes(freq)) return freq;
  const want = freqIndex(freq);
  const coarser = enabled
    .filter((f) => freqIndex(f) >= want)
    .sort((a, b) => freqIndex(a) - freqIndex(b));
  if (coarser.length) return coarser[0];
  return [...enabled].sort((a, b) => freqIndex(b) - freqIndex(a))[0];
}


/** Compute the [start, end) range for a preset code, anchored at `now`. */
function rangeForPreset(code, presets = DEFAULT_PRESETS, now = dayjs()) {
  const preset = presets.find((p) => p.value === code);
  if (preset?.range) return preset.range(now);
  const end = now.endOf('day');
  return { start: end.subtract(30, 'day'), end };
}

/**
 * Auto-snap frequency to the window length so the chart never rendered
 * 100,000 points or 2 lonely points. Caller can still override.
 */
function suggestFrequency(start, end) {
  const days = dayjs(end).diff(dayjs(start), 'day');
  if (days <= 1)    return '5min';
  if (days <= 7)    return '30min';
  // Week-to-4-months reads best daily — the old 'hourly' tier put 720
  // points on every default 30-day chart (and the Curtailment page was
  // already snapping it back to daily, so tabs disagreed on bucketing).
  if (days <= 120)  return 'daily';
  if (days <= 730)  return 'weekly';   // up to ~2 years
  if (days <= 3000) return 'monthly';  // up to ~8 years
  return 'yearly';
}

/** Friendly label for the value. */
function buildLabel(code, start, end, presets = DEFAULT_PRESETS) {
  if (code !== 'custom') {
    return presets.find((p) => p.value === code)?.label || code;
  }
  const fmt = (d) => dayjs(d).format('DD MMM');
  return `Custom · ${fmt(start)} → ${fmt(end)}`;
}


/** Public helper: build the default value for first render. */
export function defaultPeriodValue(code = '30d', presets = DEFAULT_PRESETS) {
  const { start, end } = rangeForPreset(code, presets);
  return {
    code,
    start:     start.toISOString(),
    end:       end.toISOString(),
    frequency: suggestFrequency(start, end),
    label:     buildLabel(code, start, end, presets),
  };
}


/**
 * Period state shared across screens via sessionStorage — pick "90d" on one
 * Technical tab and every other tab using the same `key` opens on the exact
 * same window. sessionStorage (not localStorage) on purpose: a persisted
 * preset holds FIXED instants, and a new browser session should re-anchor
 * "last 30 days" at today rather than wake up days stale.
 */
export function usePersistedPeriod(key, defaultCode = '30d') {
  const [period, setPeriodState] = useState(() => {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const p = JSON.parse(raw);
        if (p?.start && p?.end) return p;
      }
    } catch { /* corrupt / unavailable storage → fall through to default */ }
    return defaultPeriodValue(defaultCode);
  });
  const setPeriod = useCallback((p) => {
    setPeriodState(p);
    try { sessionStorage.setItem(key, JSON.stringify(p)); } catch { /* quota / private mode — state still updates */ }
  }, [key]);
  return [period, setPeriod];
}


// ── Component ──────────────────────────────────────────────────────────────

export default function PeriodPicker({
  value,
  onChange,
  showFrequency = true,
  availableFrequencies = FREQ_ORDER,
  granularity = null,   // finest bucket the screen's data supports; disables finer tiers
  presets = DEFAULT_PRESETS,   // quick-pick chips; screens can supply their own horizons
  showPresets = true,
  size = 'small',
}) {
  const colors = useThemeColors();
  const [popoverAnchor, setPopoverAnchor] = useState(null);

  // Local draft for the custom-range popover.
  const [draftStart, setDraftStart] = useState(
    value?.start ? dayjs(value.start) : dayjs().subtract(30, 'day')
  );
  const [draftEnd, setDraftEnd] = useState(
    value?.end ? dayjs(value.end) : dayjs()
  );

  // Frequencies this screen actually supports: the explicit allow-list,
  // further floored by `granularity` (nothing finer than the data resolution).
  const enabledFreqs = useMemo(() => {
    let list = FREQ_ORDER.filter((f) => availableFrequencies.includes(f));
    if (granularity) {
      const floor = freqIndex(granularity);
      list = list.filter((f) => freqIndex(f) >= floor);
    }
    return list;
  }, [availableFrequencies, granularity]);

  // If the incoming value (e.g. a persisted 'daily' from another tab) sits
  // below this screen's floor, snap it up to a supported tier.
  useEffect(() => {
    if (value?.frequency && enabledFreqs.length && !enabledFreqs.includes(value.frequency)) {
      onChange({ ...value, frequency: clampFrequency(value.frequency, enabledFreqs) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledFreqs]);

  function handlePresetClick(_, newCode) {
    if (!newCode) return;   // exclusive toggle; ignore deselect
    const { start, end } = rangeForPreset(newCode, presets);
    onChange({
      code: newCode,
      start: start.toISOString(),
      end:   end.toISOString(),
      frequency: clampFrequency(suggestFrequency(start, end), enabledFreqs),
      label: buildLabel(newCode, start, end, presets),
    });
  }

  function handleCustomOpen(e) {
    if (value?.start) setDraftStart(dayjs(value.start));
    if (value?.end)   setDraftEnd(dayjs(value.end));
    setPopoverAnchor(e.currentTarget);
  }

  function handleCustomApply() {
    if (!draftStart || !draftEnd || !draftStart.isValid() || !draftEnd.isValid()) return;
    if (draftEnd.isBefore(draftStart)) return;
    const start = draftStart.startOf('day');
    const end   = draftEnd.endOf('day');
    onChange({
      code: 'custom',
      start: start.toISOString(),
      end:   end.toISOString(),
      frequency: clampFrequency(suggestFrequency(start, end), enabledFreqs),
      label: buildLabel('custom', start, end, presets),
    });
    setPopoverAnchor(null);
  }

  function handleFrequencyChange(newFreq) {
    if (!value) return;
    onChange({ ...value, frequency: newFreq });
  }

  const isCustom = value?.code === 'custom';

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        {/* Preset chips */}
        {showPresets && (
        <ToggleButtonGroup
          size={size}
          exclusive
          value={isCustom ? null : value?.code || ''}
          onChange={handlePresetClick}
        >
          {presets.map((p) => (
            <ToggleButton key={p.value} value={p.value} sx={{
              border: '1px solid', borderColor: 'divider', textTransform: 'none',
              fontWeight: 600, fontSize: '0.78rem',
              '&:hover': { bgcolor: colors.hover, color: 'text.primary' },
              '&.Mui-selected': {
                color: '#F4F2EC', bgcolor: 'primary.main', borderColor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
              },
            }}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        )}

        {/* Custom button */}
        <Tooltip title="Custom date range" arrow>
          <Button
            onClick={handleCustomOpen}
            startIcon={<EventIcon sx={{ fontSize: 16 }} />}
            size={size}
            variant={isCustom ? 'contained' : 'outlined'}
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: '0.78rem',
              borderColor: 'divider',
              ...(isCustom
                ? { color: '#F4F2EC', bgcolor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark' } }
                : { color: 'text.secondary',
                    '&:hover': { borderColor: 'text.primary', color: 'text.primary' } }
              ),
            }}
          >
            {isCustom && value?.start && value?.end
              ? `${dayjs(value.start).format('DD MMM')} → ${dayjs(value.end).format('DD MMM')}`
              : 'Custom'}
          </Button>
        </Tooltip>

        {/* Frequency selector — optional. The full ladder is always shown so
            the available resolution is legible; tiers finer than the screen's
            granularity floor render disabled rather than hidden. */}
        {showFrequency && enabledFreqs.length > 1 && (
          <FormControl size={size} sx={{ minWidth: 120 }}>
            <InputLabel>Frequency</InputLabel>
            <Select
              label="Frequency"
              value={enabledFreqs.includes(value?.frequency) ? value.frequency : ''}
              onChange={(e) => handleFrequencyChange(e.target.value)}
              sx={{ fontSize: '0.82rem' }}
            >
              {ALL_FREQUENCIES.map((f) => (
                <MenuItem
                  key={f.value}
                  value={f.value}
                  disabled={!enabledFreqs.includes(f.value)}
                >
                  {f.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Custom-range popover */}
        <Popover
          open={Boolean(popoverAnchor)}
          anchorEl={popoverAnchor}
          onClose={() => setPopoverAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { mt: 1, p: 2, minWidth: 320 } } }}
        >
          <Stack spacing={2}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              Custom range
            </Typography>
            <Stack direction="row" spacing={1.5}>
              <DatePicker
                label="Start"
                value={draftStart}
                onChange={setDraftStart}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <DatePicker
                label="End"
                value={draftEnd}
                onChange={setDraftEnd}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                minDate={draftStart}
              />
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Window: {draftStart && draftEnd && draftEnd.isAfter(draftStart)
                ? `${draftEnd.diff(draftStart, 'day')} days`
                : '—'}
            </Typography>
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button onClick={() => setPopoverAnchor(null)} size="small">Cancel</Button>
              <Button
                onClick={handleCustomApply}
                variant="contained"
                size="small"
                disabled={!draftStart?.isValid() || !draftEnd?.isValid() || draftEnd.isBefore(draftStart)}
                sx={{ minWidth: 80 }}
              >
                Apply
              </Button>
            </Stack>
          </Stack>
        </Popover>
      </Stack>
    </LocalizationProvider>
  );
}
